import { useEffect, useMemo, useState } from "react";
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

import { useCollaborators } from "@/hooks";
import { useNavigateModal } from "@/router/useNavigateModal";
import { useUserStore } from "@/stores";
import { CollaboratorType } from "@/types";
import { Pagination, Table } from "@/ui";
import { getTableColumns } from "./TeachersTableColumns";

interface TeachersTableProps {
  searchQuery: string;
  titleFilter?: string;
}

export const TeachersTable = ({
  searchQuery,
  titleFilter,
}: TeachersTableProps) => {
  const navigate = useNavigate();
  const navigateModal = useNavigateModal();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const user = useUserStore((state) => state.user);

  const { useAllCollaborators } = useCollaborators();
  const { data: collaborators, isLoading } = useAllCollaborators(
    undefined,
    CollaboratorType.Teacher,
  );

  const filteredData = useMemo(() => {
    const allTeachers = collaborators?.data ?? [];
    return allTeachers.filter((c) => {
      const matchesSearch =
        !searchQuery ||
        (c.name ?? "").toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTitle =
        !titleFilter ||
        (c.title ?? "").toLowerCase().includes(titleFilter.toLowerCase());
      return matchesSearch && matchesTitle;
    });
  }, [collaborators, searchQuery, titleFilter]);

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [searchQuery, titleFilter]);

  const columns = useMemo(
    () => getTableColumns(navigateModal, user, navigate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user],
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
  });

  const totalPages = table.getPageCount();
  const currentPage = pagination.pageIndex + 1;

  return (
    <div>
      <Table table={table} isLoading={isLoading} />
      <div className="flex items-center justify-between gap-3 rounded-b-2xl border border-neutral-200 px-4">
        <span className="text-sm text-neutral-500">
          {filteredData.length}{" "}
          {filteredData.length === 1 ? "teacher" : "teachers"}
        </span>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) =>
            setPagination((p) => ({ ...p, pageIndex: page - 1 }))
          }
        />
      </div>
    </div>
  );
};
